import { useEffect, useState } from 'react'
import { api } from './api'
import { Dialogfenster } from './Dialogfenster'
import { fachlicherSatz } from './Fehlersatz'
import { beschriftung, t } from './sprache'
import type { Reihenfolge, Reihenfolgeglied } from './typen'

/**
 * Was das Fenster *Reihenfolge ändern* öffnet (7.4 Abschnitt 32, R1341): die
 * Struktur, das angeklickte Element und wie es heißt.
 */
export type Reihenfolgeziel = {
  /** positionsgliederung · kfr_zeile · bewegungsart · partnercode · ledgerelement · organisationselement · organisationsordner */
  art: string
  element: string
  titel: string
}

/** Nummer und Name, wie sie in der Struktur dastehen. */
function text(g: Reihenfolgeglied): string {
  return [g.code, beschriftung(g.namen)].filter(Boolean).join(' ')
}

/**
 * **Alphanumerisch heißt natürlich nach Nummer** (R1344) — 2 vor 10, 21a vor
 * 21b vor 22 —, bei gleicher Nummer der Name. Dieselbe Folge wie auf der
 * Rechenseite (`Natuerlich`), die nach *Übernehmen* gilt.
 */
export function alphanumerisch(glieder: Reihenfolgeglied[]): Reihenfolgeglied[] {
  const folge = (a: string, b: string) => a.localeCompare(b, 'de', { numeric: true, sensitivity: 'base' })
  return [...glieder].sort(
    (a, b) =>
      folge(a.code, b.code) || folge(beschriftung(a.namen), beschriftung(b.namen)) || a.schluessel.localeCompare(b.schluessel),
  )
}

/**
 * **Reihenfolge ändern — ein Fenster für jede Struktur** (7.4 Abschnitt 32,
 * R1341–R1345).
 *
 * Rechtsklick auf ein Element, nicht auf sein übergeordnetes: Das Fenster
 * zeigt alle Elemente derselben Ebene, das angeklickte ausgewählt. Verschoben
 * wird die Auswahl; *Alphanumerisch sortieren* ordnet einmal, *Immer
 * alphanumerisch sortiert halten* dauerhaft. Geschrieben wird erst mit
 * *Übernehmen* (R1342).
 */
export function Reihenfolgefenster({
  ziel,
  aufSchliessen,
}: {
  ziel: Reihenfolgeziel
  /** `geaendert` sagt, ob geschrieben wurde — dann zeigt das Bild die neue Folge. */
  aufSchliessen: (geaendert: boolean) => void
}) {
  const [geladen, setGeladen] = useState<Reihenfolge | null>(null)
  const [folge, setFolge] = useState<Reihenfolgeglied[]>([])
  const [auswahl, setAuswahl] = useState(ziel.element)
  const [immer, setImmer] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  useEffect(() => {
    let gilt = true
    api
      .reihenfolge(ziel.art, ziel.element)
      .then((r) => {
        if (!gilt) return
        setGeladen(r)
        setFolge(r.glieder)
        setImmer(r.immerSortiert)
        // Das Element einer Position ist ihre Position; gewählt ist, was angeklickt war.
        setAuswahl(r.glieder.some((g) => g.schluessel === ziel.element) ? ziel.element : (r.glieder[0]?.schluessel ?? ''))
      })
      .catch((f: unknown) => {
        if (gilt) setMeldung(fachlicherSatz(f) ?? t('reihenfolge.fehler'))
      })
    return () => {
      gilt = false
    }
  }, [ziel.art, ziel.element])

  const stelle = folge.findIndex((g) => g.schluessel === auswahl)
  const gezeigt = immer ? alphanumerisch(folge) : folge

  function verschiebe(nach: number) {
    if (stelle < 0 || nach < 0 || nach >= folge.length || nach === stelle) return
    const neu = [...folge]
    const [glied] = neu.splice(stelle, 1)
    neu.splice(nach, 0, glied!)
    setFolge(neu)
  }

  const veraendert =
    geladen !== null &&
    (immer !== geladen.immerSortiert || gezeigt.some((g, i) => g.schluessel !== geladen.glieder[i]?.schluessel))

  function uebernimm() {
    setLaeuft(true)
    setMeldung(null)
    api
      .reihenfolgeSetzen({ art: ziel.art, element: ziel.element, folge: gezeigt.map((g) => g.schluessel), immerSortiert: immer })
      .then(() => aufSchliessen(true))
      .catch((f: unknown) => setMeldung(fachlicherSatz(f) ?? t('reihenfolge.fehler')))
      .finally(() => setLaeuft(false))
  }

  return (
    <Dialogfenster
      titel={t('reihenfolge.titel', { element: ziel.titel })}
      weite="mittel"
      aufSchliessen={() => aufSchliessen(false)}
      fuss={
        <div className="aktionsreihe">
          <button type="button" disabled={laeuft || !veraendert} onClick={uebernimm}>
            {t('reihenfolge.uebernehmen')}
          </button>
          <button type="button" onClick={() => aufSchliessen(false)}>
            {t('beleg.abbrechen')}
          </button>
        </div>
      }
    >
      {meldung && <p className="hinweis">{meldung}</p>}
      {geladen && (
        <div className="reihenfolge">
          <ol className="reihenfolgeliste" aria-label={t('reihenfolge.liste')}>
            {gezeigt.map((g) => (
              <li key={g.schluessel}>
                <button
                  type="button"
                  className={g.schluessel === auswahl ? 'gewaehlt' : undefined}
                  aria-pressed={g.schluessel === auswahl}
                  onClick={() => setAuswahl(g.schluessel)}
                >
                  {text(g)}
                </button>
              </li>
            ))}
          </ol>

          {/* Solange der Haken steht, ordnet CoKo selbst — es gibt nichts zu verschieben (R1343). */}
          <div className="reihenfolgeknoepfe">
            <button type="button" disabled={immer || stelle <= 0} onClick={() => verschiebe(0)}>
              {t('reihenfolge.anfang')}
            </button>
            <button type="button" disabled={immer || stelle <= 0} onClick={() => verschiebe(stelle - 1)}>
              {t('reihenfolge.hoch')}
            </button>
            <button type="button" disabled={immer || stelle < 0 || stelle >= folge.length - 1} onClick={() => verschiebe(stelle + 1)}>
              {t('reihenfolge.runter')}
            </button>
            <button type="button" disabled={immer || stelle < 0 || stelle >= folge.length - 1} onClick={() => verschiebe(folge.length - 1)}>
              {t('reihenfolge.ende')}
            </button>
            <button type="button" disabled={immer} onClick={() => setFolge(alphanumerisch(folge))}>
              {t('reihenfolge.sortieren')}
            </button>
            <label className="schalter">
              <input type="checkbox" checked={immer} onChange={(e) => setImmer(e.target.checked)} />{' '}
              {t('reihenfolge.immer')}
            </label>
          </div>
        </div>
      )}
    </Dialogfenster>
  )
}
